import React, { useEffect, useState } from 'react';
import { db, collection, onSnapshot, updateDoc, doc, setDoc, handleFirestoreError, OperationType, Timestamp, getDocs, query, limit, addDoc, deleteDoc } from '../firebase';
import { Settings } from '../types';
import { useAuth } from '../App';
import { Save, Image as ImageIcon, Loader2, CheckCircle2, DollarSign, Plus, Trash2, User as UserIcon, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';
import { Signatory } from '../types';
import ConfirmationModal from '../components/ConfirmationModal';

const SettingsPage: React.FC = () => {
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [letterheadUrl, setLetterheadUrl] = useState('');
  const [signatureUrl, setSignatureUrl] = useState('');
  const [logoUrl, setLogoUrl] = useState('/Logo.png');
  const [defaultCurrency, setDefaultCurrency] = useState('TZS');
  const [vatRate, setVatRate] = useState(18);
  const [proformaInvoiceStart, setProformaInvoiceStart] = useState(26313);
  const [taxInvoiceStart, setTaxInvoiceStart] = useState(1);
  const [invoiceStart, setInvoiceStart] = useState(1);
  const [companyName, setCompanyName] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [tin, setTin] = useState('');
  const [vrn, setVrn] = useState('');
  const { isAdmin, profile } = useAuth();
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(profile?.photoURL || '');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [signatories, setSignatories] = useState<Signatory[]>([]);
  const [isSignatoryModalOpen, setIsSignatoryModalOpen] = useState(false);
  const [isSubmittingSignatory, setIsSubmittingSignatory] = useState(false);
  const [signatoryFormData, setSignatoryFormData] = useState({
    name: '',
    title: '',
    signatureUrl: ''
  });

  useEffect(() => {
    const unsubscribeSignatories = onSnapshot(collection(db, 'signatories'), (snapshot) => {
      setSignatories(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Signatory)));
    });
    return () => unsubscribeSignatories();
  }, []);

  const handleSaveSignatory = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmittingSignatory(true);
    try {
      await addDoc(collection(db, 'signatories'), {
        ...signatoryFormData,
        createdAt: Timestamp.now()
      });
      toast.success('Signatory added successfully');
      setIsSignatoryModalOpen(false);
      setSignatoryFormData({ name: '', title: '', signatureUrl: '' });
    } catch (error) {
      toast.error('Failed to add signatory');
    } finally {
      setIsSubmittingSignatory(false);
    }
  };

  const handleDeleteSignatory = (id: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Signatory',
      message: 'Are you sure you want to delete this signatory?',
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'signatories', id));
          toast.success('Signatory deleted');
        } catch (error) {
          toast.error('Failed to delete signatory');
        }
      }
    });
  };

  useEffect(() => {
    if (profile?.photoURL) {
      setProfilePhotoUrl(profile.photoURL);
    }
  }, [profile]);

  const handleSaveProfile = async () => {
    if (!profile) return;
    setIsSavingProfile(true);
    try {
      await updateDoc(doc(db, 'users', profile.uid), {
        photoURL: profilePhotoUrl,
        updatedAt: Timestamp.now()
      });
      toast.success('Profile updated successfully');
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'users');
      toast.error('Failed to update profile');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleProfilePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhotoUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    const q = query(collection(db, 'settings'), limit(1));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const settingsData = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as Settings;
        setSettings(settingsData);
        setLetterheadUrl(settingsData.letterheadUrl || '');
        setLogoUrl(settingsData.logoUrl || '');
        setSignatureUrl(settingsData.signatureUrl || '');
        setDefaultCurrency(settingsData.defaultCurrency || 'TZS');
        setVatRate(settingsData.vatRate ?? 18);
        setProformaInvoiceStart(settingsData.proformaInvoiceStart ?? 26313);
        setTaxInvoiceStart(settingsData.taxInvoiceStart ?? 1);
        setInvoiceStart(settingsData.invoiceStart ?? 1);
        setCompanyName(settingsData.companyName || '');
        setCompanyAddress(settingsData.companyAddress || '');
        setCompanyPhone(settingsData.companyPhone || '');
        setCompanyEmail(settingsData.companyEmail || '');
        setCompanyWebsite(settingsData.companyWebsite || '');
        setTin(settingsData.tin || '');
        setVrn(settingsData.vrn || '');
      }
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, 'settings');
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleSave = async () => {
    if (!isAdmin) {
      toast.error('Only admins can update settings');
      return;
    }

    setIsSaving(true);
    try {
      if (settings) {
        await updateDoc(doc(db, 'settings', settings.id), {
          companyName,
          companyAddress,
          companyPhone,
          companyEmail,
          companyWebsite,
          tin,
          vrn,
          letterheadUrl,
          logoUrl,
          signatureUrl,
          defaultCurrency,
          vatRate,
          proformaInvoiceStart,
          taxInvoiceStart,
          invoiceStart,
          updatedAt: Timestamp.now()
        });
      } else {
        const newSettingsRef = doc(collection(db, 'settings'));
        await setDoc(newSettingsRef, {
          companyName,
          companyAddress,
          companyPhone,
          companyEmail,
          companyWebsite,
          tin,
          vrn,
          letterheadUrl,
          logoUrl,
          signatureUrl,
          defaultCurrency,
          vatRate,
          proformaInvoiceStart,
          taxInvoiceStart,
          invoiceStart,
          updatedAt: Timestamp.now()
        });
      }
      toast.success('Settings saved successfully');
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'settings');
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLetterheadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLetterheadUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSignatureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignatureUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64">Loading settings...</div>;

  return (
    <div className="max-w-4xl mx-auto space-y-8 pb-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Settings</h1>
          <p className="text-slate-500">Manage your profile and system configuration</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8">
        {/* User Profile Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-8"
        >
          <div className="flex items-center gap-3 pb-4 border-b border-slate-50">
            <ImageIcon className="w-6 h-6 text-blue-800" />
            <h2 className="text-xl font-bold text-slate-900">My Profile</h2>
          </div>

          <div className="flex flex-col md:flex-row gap-8 items-center md:items-start">
            <div className="relative group">
              <div className="w-32 h-32 bg-slate-100 rounded-full overflow-hidden border-4 border-white shadow-lg flex items-center justify-center">
                {profilePhotoUrl ? (
                  <img src={profilePhotoUrl} alt="Profile" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <ImageIcon className="w-12 h-12 text-slate-300" />
                )}
              </div>
              <label className="absolute bottom-0 right-0 p-2 bg-blue-800 text-white rounded-full shadow-lg cursor-pointer hover:bg-blue-900 transition-all">
                <ImageIcon className="w-4 h-4" />
                <input type="file" className="hidden" accept="image/*" onChange={handleProfilePhotoChange} />
              </label>
            </div>

            <div className="flex-1 space-y-4 w-full">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Display Name</label>
                  <input type="text" value={profile?.displayName || ''} disabled className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl text-slate-500 cursor-not-allowed" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Email Address</label>
                  <input type="text" value={profile?.email || ''} disabled className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl text-slate-500 cursor-not-allowed" />
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={handleSaveProfile}
                  disabled={isSavingProfile}
                  className="flex items-center justify-center gap-2 bg-blue-800 text-white px-6 py-2.5 rounded-xl font-bold hover:bg-blue-900 transition-all shadow-lg shadow-blue-200 disabled:opacity-50"
                >
                  {isSavingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Profile
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {isAdmin && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-12"
          >
            {/* Company Information Section */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 pb-4 border-b border-slate-50">
                <ImageIcon className="w-6 h-6 text-blue-800" />
                <h2 className="text-xl font-bold text-slate-900">Company Information</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Company Name</label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="e.g. Acme Corporation"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Website</label>
                  <input
                    type="text"
                    value={companyWebsite}
                    onChange={(e) => setCompanyWebsite(e.target.value)}
                    placeholder="e.g. www.acme.com"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Company Address</label>
                  <textarea
                    value={companyAddress}
                    onChange={(e) => setCompanyAddress(e.target.value)}
                    placeholder="Physical address"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold min-h-[100px]"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Business Email</label>
                  <input
                    type="email"
                    value={companyEmail}
                    onChange={(e) => setCompanyEmail(e.target.value)}
                    placeholder="contact@acme.com"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Phone Number</label>
                  <input
                    type="text"
                    value={companyPhone}
                    onChange={(e) => setCompanyPhone(e.target.value)}
                    placeholder="+255 123 456 789"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">TIN (Taxpayer Identification Number)</label>
                  <input
                    type="text"
                    value={tin}
                    onChange={(e) => setTin(e.target.value)}
                    placeholder="TIN Number"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">VRN (VAT Registration Number)</label>
                  <input
                    type="text"
                    value={vrn}
                    onChange={(e) => setVrn(e.target.value)}
                    placeholder="VRN Number"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                  />
                </div>
              </div>
            </div>

            {/* Company Logo Section */}
        <div className="space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b border-slate-50">
            <ImageIcon className="w-6 h-6 text-blue-800" />
            <h2 className="text-xl font-bold text-slate-900">Company Logo</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Upload your company logo. This will be used in the application header and on invoices.
                Recommended size: Square or circular (e.g. 512x512px).
              </p>
              
              <div className="flex flex-col gap-4">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-200 rounded-2xl cursor-pointer hover:bg-slate-50 transition-all">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <ImageIcon className="w-8 h-8 text-slate-400 mb-2" />
                    <p className="text-sm text-slate-500">Click to upload logo</p>
                  </div>
                  <input type="file" className="hidden" accept="image/*" onChange={handleLogoChange} />
                </label>
                
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Or Logo URL</label>
                  <input
                    type="text"
                    value={logoUrl}
                    onChange={(e) => setLogoUrl(e.target.value)}
                    placeholder="https://example.com/logo.png"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Preview</label>
              <div className="w-32 h-32 bg-slate-100 rounded-2xl overflow-hidden border border-slate-200 flex items-center justify-center relative mx-auto md:mx-0">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo Preview" className="w-full h-full object-contain" referrerPolicy="no-referrer" />
                ) : (
                  <div className="text-slate-400 flex flex-col items-center gap-2">
                    <ImageIcon className="w-6 h-6" />
                    <span className="text-xs">No logo</span>
                  </div>
                )}
              </div>
              {logoUrl && (
                <div className="flex items-center gap-2 text-emerald-600 text-xs font-medium justify-center md:justify-start">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Logo ready</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Company Letterhead Section */}
        <div className="space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b border-slate-50">
            <ImageIcon className="w-6 h-6 text-blue-800" />
            <h2 className="text-xl font-bold text-slate-900">Company Letterhead</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Upload your company letterhead image. This will be used at the top of all generated PDF reports.
                Recommended size: 2000x400px (approx 5:1 ratio).
              </p>
              
              <div className="flex flex-col gap-4">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-200 rounded-2xl cursor-pointer hover:bg-slate-50 transition-all">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <ImageIcon className="w-8 h-8 text-slate-400 mb-2" />
                    <p className="text-sm text-slate-500">Click to upload letterhead</p>
                  </div>
                  <input type="file" className="hidden" accept="image/*" onChange={handleLetterheadChange} />
                </label>
                
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Or Image URL</label>
                  <input
                    type="text"
                    value={letterheadUrl}
                    onChange={(e) => setLetterheadUrl(e.target.value)}
                    placeholder="https://example.com/letterhead.png"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Preview</label>
              <div className="w-full aspect-[5/1] bg-slate-100 rounded-2xl overflow-hidden border border-slate-200 flex items-center justify-center relative">
                {letterheadUrl ? (
                  <img src={letterheadUrl} alt="Letterhead Preview" className="w-full h-full object-contain" referrerPolicy="no-referrer" />
                ) : (
                  <div className="text-slate-400 flex flex-col items-center gap-2">
                    <ImageIcon className="w-6 h-6" />
                    <span className="text-xs">No image selected</span>
                  </div>
                )}
              </div>
              {letterheadUrl && (
                <div className="flex items-center gap-2 text-emerald-600 text-xs font-medium">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Letterhead ready for use</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Authorized Signature Section */}
        <div className="space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b border-slate-50">
            <ImageIcon className="w-6 h-6 text-blue-800" />
            <h2 className="text-xl font-bold text-slate-900">Default Authorized Signature</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Upload the default signature image for invoices. This can be overridden per invoice if needed.
              </p>
              
              <div className="flex flex-col gap-4">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-200 rounded-2xl cursor-pointer hover:bg-slate-50 transition-all">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <ImageIcon className="w-8 h-8 text-slate-400 mb-2" />
                    <p className="text-sm text-slate-500">Click to upload signature</p>
                  </div>
                  <input type="file" className="hidden" accept="image/*" onChange={handleSignatureChange} />
                </label>
                
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Or Signature URL</label>
                  <input
                    type="text"
                    value={signatureUrl}
                    onChange={(e) => setSignatureUrl(e.target.value)}
                    placeholder="https://example.com/signature.png"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Preview</label>
              <div className="w-32 h-16 bg-white rounded-2xl overflow-hidden border border-slate-200 flex items-center justify-center relative mx-auto md:mx-0">
                {signatureUrl ? (
                  <img src={signatureUrl} alt="Signature Preview" className="w-full h-full object-contain" referrerPolicy="no-referrer" />
                ) : (
                  <div className="text-slate-400 flex flex-col items-center gap-1">
                    <ImageIcon className="w-4 h-4" />
                    <span className="text-[10px]">No signature</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Financial Settings Section */}
        <div className="space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b border-slate-50">
            <DollarSign className="w-6 h-6 text-blue-800" />
            <h2 className="text-xl font-bold text-slate-900">Financial Settings</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Configure default financial parameters for the application.
              </p>
              
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Default Currency</label>
                <select
                  value={defaultCurrency}
                  onChange={(e) => setDefaultCurrency(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                >
                  <option value="TZS">TZS - Tanzanian Shilling</option>
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - British Pound</option>
                  <option value="KES">KES - Kenyan Shilling</option>
                  <option value="UGX">UGX - Ugandan Shilling</option>
                  <option value="ZAR">ZAR - South African Rand</option>
                </select>
                <p className="text-[10px] text-slate-400 italic">This currency will be pre-selected for new clients and invoices.</p>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Default VAT Rate (%)</label>
                <input
                  type="number"
                  value={vatRate}
                  onChange={(e) => setVatRate(Number(e.target.value))}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                  min="0"
                  max="100"
                  step="0.1"
                />
                <p className="text-[10px] text-slate-400 italic">Default VAT rate applied to new invoices. Standard is 18%.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Formal Invoice Start</label>
                  <input
                    type="number"
                    value={invoiceStart}
                    onChange={(e) => setInvoiceStart(Number(e.target.value))}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                    min="1"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Proforma Invoice Start</label>
                  <input
                    type="number"
                    value={proformaInvoiceStart}
                    onChange={(e) => setProformaInvoiceStart(Number(e.target.value))}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                    min="1"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Tax Invoice Start</label>
                  <input
                    type="number"
                    value={taxInvoiceStart}
                    onChange={(e) => setTaxInvoiceStart(Number(e.target.value))}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                    min="1"
                  />
                </div>
              </div>
              <p className="text-[10px] text-slate-400 italic mt-2">These will be the base numbers for the next generated invoices of each type.</p>
            </div>
          </div>
        </div>

        {/* Authorized Signatories Section */}
        <div className="space-y-6 pt-6 border-t border-slate-100">
          <div className="flex items-center justify-between pb-4 border-b border-slate-50">
            <div className="flex items-center gap-3">
              <UserIcon className="w-6 h-6 text-blue-800" />
              <h2 className="text-xl font-bold text-slate-900">Authorized Signatories</h2>
            </div>
            <button
              onClick={() => setIsSignatoryModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-800 text-white rounded-xl text-xs font-bold hover:bg-blue-900 transition-all shadow-sm"
            >
              <Plus className="w-4 h-4" />
              Add Signatory
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {signatories.map((sig) => (
              <div key={sig.id} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between group">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white rounded-xl border border-slate-200 flex items-center justify-center overflow-hidden">
                    {sig.signatureUrl ? (
                      <img src={sig.signatureUrl} alt="Signature" className="w-full h-full object-contain" />
                    ) : (
                      <UserIcon className="w-6 h-6 text-slate-300" />
                    )}
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 uppercase tracking-wide text-sm">{sig.name}</p>
                    <p className="text-xs text-slate-500 font-medium">{sig.title}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteSignatory(sig.id)}
                  className="p-2 text-slate-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            {signatories.length === 0 && (
              <div className="md:col-span-2 py-8 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                <p className="text-slate-400 font-medium italic">No authorized signatories added yet.</p>
              </div>
            )}
          </div>
        </div>

        <AnimatePresence>
          {isSignatoryModalOpen && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsSignatoryModalOpen(false)}
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl p-8"
              >
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-2xl font-bold text-slate-900">Add Signatory</h2>
                  <button onClick={() => setIsSignatoryModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                    <X className="w-6 h-6 text-slate-400" />
                  </button>
                </div>

                <form onSubmit={handleSaveSignatory} className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">Full Name *</label>
                    <input
                      required
                      type="text"
                      value={signatoryFormData.name}
                      onChange={(e) => setSignatoryFormData({ ...signatoryFormData, name: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                      placeholder="e.g. Fabian Kisaki"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">Job Title *</label>
                    <input
                      required
                      type="text"
                      value={signatoryFormData.title}
                      onChange={(e) => setSignatoryFormData({ ...signatoryFormData, title: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold"
                      placeholder="e.g. Managing Director"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">Signature Image (Optional)</label>
                    <div className="flex flex-col gap-4">
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-200 rounded-2xl cursor-pointer hover:bg-slate-50 transition-all">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <ImageIcon className="w-8 h-8 text-slate-400 mb-2" />
                          <p className="text-sm text-slate-500">Click to upload signature</p>
                        </div>
                        <input 
                          type="file" 
                          className="hidden" 
                          accept="image/*" 
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setSignatoryFormData({ ...signatoryFormData, signatureUrl: reader.result as string });
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                        />
                      </label>
                      {signatoryFormData.signatureUrl && (
                        <div className="relative w-32 h-16 bg-white border border-slate-200 rounded-2xl overflow-hidden self-center">
                          <img src={signatoryFormData.signatureUrl} alt="Signature Preview" className="w-full h-full object-contain" />
                          <button
                            type="button"
                            onClick={() => setSignatoryFormData({ ...signatoryFormData, signatureUrl: '' })}
                            className="absolute -top-1 -right-1 bg-red-500 text-white p-1 rounded-full shadow-lg"
                          >
                            <Trash2 className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmittingSignatory}
                    className="w-full py-4 bg-blue-800 text-white rounded-2xl font-bold hover:bg-blue-900 transition-all shadow-lg shadow-blue-200 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isSubmittingSignatory ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                    Save Signatory
                  </button>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        <div className="pt-6 border-t border-slate-50 flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center justify-center gap-2 bg-blue-800 text-white px-8 py-3 rounded-2xl font-bold hover:bg-blue-900 transition-all shadow-lg shadow-blue-200 disabled:opacity-50"
          >
            {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            Save Settings
          </button>
        </div>
      </motion.div>
        )}
      </div>

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel="YES"
        cancelLabel="NO"
      />
    </div>
  );
};

export default SettingsPage;
